import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { WifiBadge } from "../components/StatusBadge";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Plus, LogIn, LogOut, Ban, QrCode, Printer, Copy, ArrowRight, Pencil, RefreshCw, Mail, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Reservations() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [items, setItems] = useState([]);
  const [structures, setStructures] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/reservations${p}`).then((r) => setItems(r.data));
  };
  useEffect(() => {
    load();
    api.get("/structures").then((r) => setStructures(r.data));
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/profiles${p}`).then((r) => setProfiles(r.data));
  }, [effectiveStructure]);

  // Auto-refresh finché un provisioning è in corso (aggiornamento in tempo reale)
  useEffect(() => {
    if (!items.some((r) => r.provisioning_status === "in_progress")) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [items]);

  const newRes = () => {
    const sid = effectiveStructure || structures[0]?.id || "";
    setForm({ structure_id: sid, guest_name: "", guest_email: "", accommodation: "", checkin_date: "", checkout_date: "", profile_id: "", booking_ref: "" });
    setEditId(null);
    setOpen(true);
  };

  const editRes = (r) => {
    setForm({ structure_id: r.structure_id, guest_name: r.guest_name, guest_email: r.guest_email || "", accommodation: r.accommodation || "", checkin_date: r.checkin_date, checkout_date: r.checkout_date, profile_id: r.profile_id, booking_ref: r.booking_ref || "" });
    setEditId(r.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/reservations/${editId}`, {
          guest_name: form.guest_name, guest_email: form.guest_email, accommodation: form.accommodation,
          checkin_date: form.checkin_date, checkout_date: form.checkout_date, profile_id: form.profile_id,
        });
        toast.success(`${t("common.save")} ✓ — ${t("res.same_code")}`);
      } else {
        const { data } = await api.post("/reservations", form);
        toast.success(`${t("wifi.code")}: ${data.wifi_code} (${t("wifi.pending")})`);
      }
      setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const act = async (r, action) => {
    try {
      await api.post(`/reservations/${r.id}/${action}`);
      toast.success("✓ " + action);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const showQr = async (r) => {
    const { data } = await api.get(`/reservations/${r.id}/qr`);
    setQr(data);
  };

  const regenerate = async (r) => {
    try {
      const { data } = await api.post(`/reservations/${r.id}/regenerate-code`);
      toast.success(`${t("res.new_code")}: ${data.wifi_code}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const sendEmail = async (r) => {
    if (!r.guest_email) { toast.error(t("res.no_email")); return; }
    try {
      await api.post(`/reservations/${r.id}/send-email`);
      toast.success(t("res.email_sent"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const wifiAct = async (r, kind) => {
    try {
      await api.post(`/reservations/${r.id}/wifi/${kind}`);
      toast.success(kind === "create" ? t("res.wifi_created") : t("res.wifi_deactivated"));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const formProfiles = profiles.filter((p) => !form || p.structure_id === form.structure_id);

  return (
    <div data-testid="reservations-page">
      <PageHeader title={t("res.title")} subtitle={t("res.rule")} action={
        <button data-testid="new-reservation-btn" onClick={newRes} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {t("res.new")}
        </button>} />

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`reservation-card-${r.id}`} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5">
                <h3 className="font-display font-semibold">{r.guest_name}</h3>
                <span className="text-xs text-slate-500">· {r.accommodation}</span>
                {r.booking_ref && <span className="text-xs font-mono text-slate-600">{r.booking_ref}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">{r.checkin_date} → {r.checkout_date} · {r.profile_name}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("wifi.code")}</div>
                <div className="font-mono text-lg font-bold text-cyan-400 tracking-widest">{r.wifi_code}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <WifiBadge status={r.wifi_status} live={r.wifi_status === "active"} />
                {r.provisioning_status && <ProvBadge status={r.provisioning_status} detail={r.provisioning_detail} />}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {r.wifi_status === "pending" && (
                <button data-testid={`checkin-btn-${r.id}`} onClick={() => act(r, "checkin")} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600/15 text-emerald-300 ring-1 ring-emerald-600/30 hover:bg-emerald-600/25 px-3 py-1.5 text-xs font-medium transition-colors"><LogIn className="h-3.5 w-3.5" /> {t("res.do_checkin")}</button>
              )}
              {r.wifi_status === "active" && (
                <button data-testid={`checkout-btn-${r.id}`} onClick={() => act(r, "checkout")} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600/15 text-rose-300 ring-1 ring-rose-600/30 hover:bg-rose-600/25 px-3 py-1.5 text-xs font-medium transition-colors"><LogOut className="h-3.5 w-3.5" /> {t("res.do_checkout")}</button>
              )}
              {r.wifi_status === "pending" && (
                <button data-testid={`cancel-btn-${r.id}`} onClick={() => act(r, "cancel")} className="inline-flex items-center gap-1.5 rounded-lg hover:bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-400 transition-colors"><Ban className="h-3.5 w-3.5" /> {t("res.cancel_res")}</button>
              )}
              <button data-testid={`edit-btn-${r.id}`} onClick={() => editRes(r)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><Pencil className="h-3.5 w-3.5" /> {t("common.edit")}</button>
              {r.wifi_status !== "active" && (
                <button data-testid={`wifi-create-${r.id}`} onClick={() => wifiAct(r, "create")} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600/15 text-emerald-300 ring-1 ring-emerald-600/30 hover:bg-emerald-600/25 px-3 py-1.5 text-xs font-medium transition-colors"><Wifi className="h-3.5 w-3.5" /> {t("res.wifi_create")}</button>
              )}
              {r.wifi_status === "active" && (
                <button data-testid={`wifi-deact-${r.id}`} onClick={() => wifiAct(r, "deactivate")} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600/15 text-rose-300 ring-1 ring-rose-600/30 hover:bg-rose-600/25 px-3 py-1.5 text-xs font-medium transition-colors"><WifiOff className="h-3.5 w-3.5" /> {t("res.wifi_deactivate")}</button>
              )}
              <button data-testid={`regen-btn-${r.id}`} onClick={() => regenerate(r)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><RefreshCw className="h-3.5 w-3.5" /> {t("res.regenerate")}</button>
              <button data-testid={`email-btn-${r.id}`} onClick={() => sendEmail(r)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><Mail className="h-3.5 w-3.5" /> Email</button>
              <button data-testid={`qr-btn-${r.id}`} onClick={() => showQr(r)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><QrCode className="h-3.5 w-3.5" /> {t("res.qr")}</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-slate-500 py-10">{t("common.none")}</div>}
      </div>

      {/* Create modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("res.edit") : t("res.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("nav.structures")}</label>
                <select data-testid="res-structure-select" value={form.structure_id} onChange={(e) => setForm({ ...form, structure_id: e.target.value, profile_id: "" })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                  {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.guest")}</label>
                <input data-testid="res-input-guest" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.accommodation")}</label>
                <input data-testid="res-input-accommodation" value={form.accommodation} onChange={(e) => setForm({ ...form, accommodation: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.checkin")}</label>
                <input data-testid="res-input-checkin" type="date" value={form.checkin_date} onChange={(e) => setForm({ ...form, checkin_date: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.checkout")}</label>
                <input data-testid="res-input-checkout" type="date" value={form.checkout_date} onChange={(e) => setForm({ ...form, checkout_date: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.email")}</label>
                <input data-testid="res-input-email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.ref")}</label>
                <input data-testid="res-input-ref" value={form.booking_ref} onChange={(e) => setForm({ ...form, booking_ref: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div className="col-span-2"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("res.profile")}</label>
                <select data-testid="res-profile-select" value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                  <option value="">—</option>
                  {formProfiles.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.download_mbps}/{p.upload_mbps})</option>)}
                </select></div>
            </div>
          )}
          <div className="rounded-lg bg-blue-600/10 ring-1 ring-blue-600/20 px-3 py-2 text-xs text-blue-300 flex items-center gap-2">
            <ArrowRight className="h-3.5 w-3.5" /> {t("res.rule")}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-400">{t("common.cancel")}</button>
            <button data-testid="save-reservation-btn" onClick={save} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.create")}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR modal */}
      <Dialog open={!!qr} onOpenChange={(v) => !v && setQr(null)}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-sm">
          <DialogHeader><DialogTitle className="font-display">{t("res.qr")}</DialogTitle></DialogHeader>
          {qr && (
            <div data-testid="qr-voucher" id="qr-voucher" className="rounded-xl bg-white text-slate-900 p-6 text-center">
              <div className="text-sm font-bold uppercase tracking-wide">{qr.structure_name}</div>
              <div className="text-xs text-slate-500 mb-3">{qr.ssid}</div>
              <img src={qr.qr} alt="QR" className="mx-auto w-44 h-44" />
              <div className="mt-3 text-xs text-slate-500">{t("wifi.code")}</div>
              <div className="font-mono text-2xl font-bold tracking-[0.3em]">{qr.code}</div>
              <div className="mt-2 text-xs text-slate-600">{qr.guest_name} · {qr.accommodation}</div>
              <div className="text-xs text-slate-600">{qr.checkin_date} → {qr.checkout_date}</div>
              <div className="mt-2 inline-flex"><WifiVoucherBadge status={qr.status} /></div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button data-testid="copy-code-btn" onClick={() => { navigator.clipboard.writeText(qr.code); toast.success("✓"); }} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-sm"><Copy className="h-4 w-4" /> {t("wifi.code")}</button>
            <button data-testid="print-qr-btn" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-semibold"><Printer className="h-4 w-4" /> {t("res.qr")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WifiVoucherBadge({ status }) {
  const m = { pending: ["#F59E0B", "IN ATTESA"], active: ["#10B981", "ATTIVO"], disabled: ["#EF4444", "DISABILITATO"] };
  const [c, l] = m[status] || m.pending;
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: c, background: c + "22" }}>{l}</span>;
}

function ProvBadge({ status, detail }) {
  const m = {
    in_progress: ["#F59E0B", "Invio al router…", true],
    done: ["#10B981", "Sul router ✓", false],
    partial: ["#F59E0B", "Parziale", false],
    error: ["#EF4444", "Errore router", false],
    simulated: ["#64748B", "Simulato", false],
    removed: ["#64748B", "Rimosso", false],
  };
  const [c, l, spin] = m[status] || ["#64748B", status, false];
  return (
    <span title={detail || ""} data-testid="prov-badge" className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: c, background: c + "22" }}>
      {spin && <RefreshCw className="h-3 w-3 animate-spin" />} {l}
    </span>
  );
}
